import React, { useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../lib';
import { SYNC_STAGE_CONFIG, SYNC_STAGES } from '../../constants/syncStages';
import { getAppDisplayName } from '../../utils/appUtils';
import styles from './SyncToast.module.scss';

export function SyncToast() {
  // Don't render the toast - sync status is now shown in the header
  // This component is still used for the modal functionality via SyncModalPortal export
  return null;
}

// Portal wrapper for the modal
export function SyncModalPortal(props) {
  return createPortal(
    <SyncModal {...props} />,
    document.body
  );
}

// Embedded version without overlay (for dashboard card)
export function SyncModalEmbedded(props) {
  return <SyncModal {...props} embedded={true} />;
}

// Modal component
function SyncModal({ onClose, syncStatus, currentMessage, appName, progress, syncHistory, currentStage, embedded = false }) {
  // Lock body scroll when modal is open (but not for embedded version)
  useEffect(() => {
    if (!embedded) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [embedded]);
  
  // Get display name for the app
  const displayAppName = getAppDisplayName(appName);
  
  const syncStages = Object.entries(SYNC_STAGE_CONFIG).map(([id, config]) => ({
    id,
    ...config
  })).filter(stage => stage.id !== SYNC_STAGES.ERROR); // Don't show error stage in progress flow

  function getStageStatus(stage) {
    const stageIndex = syncStages.findIndex(s => s.id === stage.id);
    
    // If sync is complete, show all stages as completed
    if (syncStatus === 'complete') {
      return 'completed';
    }
    
    // If viewing historical data and last stage was COMPLETE, show all as completed
    if (syncHistory && syncHistory.length > 0) {
      const lastHistoryItem = syncHistory[syncHistory.length - 1];
      if (lastHistoryItem.stage === 'complete') {
        return 'completed';
      }
      
      // Check if this specific stage appeared in the history
      const stageInHistory = syncHistory.find(item => item.stage === stage.id);
      if (stageInHistory) {
        return 'completed';
      }
    }
    
    // If no current stage, check against sync history
    if (!currentStage) {
      // If we have history but no current stage, we're viewing historical data
      if (syncHistory && syncHistory.length > 0) {
        const stageInHistory = syncHistory.find(item => item.stage === stage.id);
        if (stageInHistory) {
          return 'completed';
        }
      }
      return 'pending';
    }
    
    const currentIndex = syncStages.findIndex(s => s.id === currentStage);
    
    // If sync had an error, show stages up to the error as completed
    if (syncStatus === 'error') {
      if (stageIndex < currentIndex) return 'completed';
      if (stageIndex === currentIndex) return 'error';
      return 'pending';
    }
    
    // Normal progression logic
    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) return 'active';
    return 'pending';
  }

  function getStageIcon(stage) {
    const status = getStageStatus(stage);
    if (status === 'completed') return '✓';
    if (status === 'active') return '⏳';
    if (status === 'error') return '✕';
    return stage.icon;
  }

  const modalContent = (
    <div className={`${styles.modal} ${embedded ? styles.embedded : ''}`} onClick={(e) => e.stopPropagation()}>
      <div className={styles.modalHeader}>
        <h3>{displayAppName && `${displayAppName} `}Data Sync Progress</h3>
        {!embedded && <button onClick={onClose} className={styles.closeButton}>×</button>}
      </div>
      
      <div className={styles.modalContent}>
        {(syncStatus !== 'idle' || (syncHistory && syncHistory.length > 0)) && (
          <div className={styles.stagesContainer}>
            {syncStages.map(stage => (
              <div 
                key={stage.id} 
                className={`${styles.stage} ${styles[getStageStatus(stage)]}`}
              >
                <div className={styles.stageIcon}>
                  <span>{getStageIcon(stage)}</span>
                </div>
                <div className={styles.stageContent}>
                  <h4>{stage.title}</h4>
                </div>
              </div>
            ))}
          </div>
        )}

        {syncHistory.length > 0 && (
          <div className={styles.historySection}>
            <h4>Recent Activity</h4>
            <div className={styles.historyList}>
              {syncHistory.slice(-15).reverse().map(item => (
                <div 
                  key={item.id} 
                  className={`${styles.historyItem} ${item.error ? styles.error : ''}`}
                >
                  <span className={styles.timestamp}>
                    {item.timestamp instanceof Date ? item.timestamp.toLocaleTimeString() : new Date(item.timestamp || Date.now()).toLocaleTimeString()}
                  </span>
                  <span className={styles.message}>
                    {item.message}
                  </span>
                  {item.record_count && (
                    <span className={styles.detail}>
                      {item.record_count} records
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {syncHistory.length === 0 && (
          <div className={styles.noHistory}>
            <p>No sync history available yet.</p>
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) {
    return modalContent;
  }

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      {modalContent}
    </div>
  );
}